import {
  Dispatch,
  SetStateAction,
} from 'react';

export type Nullable<T = undefined> = T | null;

// Defines the type for HTTP GET query parameters
export type QueryParams = { [key: string]: string | Array<string> | undefined };

export enum RequestStatus {
  IDLE = 'idle',
  LOADING = 'loading',
  SUCCEEDED = 'succeeded',
  FAILED = 'failed',
}

export type Dispatcher<S> = Dispatch<SetStateAction<S>>;
export interface TextElementStyled {
  textcolor?: string;
}

export interface BackgroundElement {
  backgroundcolor?: string;
  textcolor?: string;
}
export interface MessageElementStyled {
  messagesent: 0 | 1;
  backgroundcolor?: string;
}

export interface ContentBlockType {
  isAllowEdit: boolean;
  name: string;
}

export interface CustomField {
  fieldType: string;
  options: Array<string>;
  label: string;
  uuid: string;
  description?: string;
}

export interface MultiOptionValue {
  label: string;
  value: string;
}

export interface CustomFieldWithValue extends CustomField {
  value?: string;
  values?: Array<MultiOptionValue>;
}

export interface CustomFieldValues {
  customField: string;
  value: string;
}

export interface ChannelOptIn {
  checkboxText: string;
  isChecked: Nullable<boolean>; // Should not be nullable indefinitely
  optIn: Nullable<boolean>;
  optInText: string;
}

export interface OptInOptions {
  defaultOptIn: Nullable<boolean>;
  defaultOptInText: string;
  email: ChannelOptIn;
  emailCheckboxText: string;
  textCheckboxText: string;
  textSms: ChannelOptIn;
}

export interface AdvocateCount {
  advocates: number;
}

/**
 * Represents a field in the form schema
 */
export interface FormSchemaField {
  [key: string]: unknown;
}

/**
 * Schema configuration for action centers and signup forms
 * Defines CSS class and form field configurations
 */
export interface FormSchema {
  /** CSS class name applied to the form */
  cssClass: string;
  /** Array of field configurations */
  fields: Array<FormSchemaField>;
}

/**
 * Replaces separate fields, adds customization and redirect.
 */
export interface NextSteps {
  url: Nullable<string>;
  buttonText: string;
  shouldRedirect: boolean;
}

interface DataDetails {
  backgroundcolor?: string;
  customFields: Array<CustomField>;
  facebookPixelEnabled: boolean;
  facebookPixelId: string;
  googleAnalyticsEnabled: boolean;
  googleAnalyticsId: string;
  headlineConfig: {
    backgroundColor: string;
    buttonColor: string;
    descriptionBody?: string;
    descriptionHeadlineText: string;
    headlineText: string;
    textArea: string;
    textColor: string;
    secondHeadlineText: string;
    vimeoCode: string;
    youtubeId: string | false;
  };
  isDonateButton: boolean;
  linkDonate: Nullable<string>;
  logo: string;
  name?: string;
  optIn: OptInOptions;
  organization: {
    name: string;
    city?: string;
    state?: string;
    uuid?: string;
    websiteUrl?: string;
    privacyPolicyUrl?: string;
  };
  socialShareButtonEnabled: boolean;
  schema?: FormSchema;
  nextSteps: NextSteps | { [key: string]: never };
  status: 'draft' | 'live' | 'stopped';
  thankYouMessage: string;
  uuid: string;
}

export enum SignupVersions {
  VERSION_2 = 'version2',
  VERSION_3 = 'version3',
  VERSION_4 = 'version4',
}

export interface SignupDetails extends DataDetails {
  signupPageImage: string;
  submitButtonText: string;
  version: SignupVersions;
}

export interface WidgetListItem {
  name?: string;
  title: string;
  uuid: string;
  subheadline?: string;
  image?: string;
}

export enum ACVersions {
  VERSION_1 = 'version1',
  VERSION_2 = 'version2',
  VERSION_3 = 'version3',
  VERSION_4 = 'version4',
}

export interface ActionCenterDetails extends DataDetails {
  emailChannel: boolean;
  isGeocoded: boolean;
  landingPageImage: string;
  messageBody: {
    isAllowPersonalMessage: boolean;
    prefilledMessages: Array<{
      isAllowEdit: boolean;
      name: string;
    }>;
  };
  mode: string;
  prefilledMessages?: Nullable<Array<SubjectMessagePair>>;
  phoneChannel: boolean;
  messagingType: MessagePairTypes;
  prefixes: ['Ms.', 'Mr.', 'Mrs.', 'Dr.', 'Miss'];
  showAdvocatesCount: boolean;
  subjectBody: {
    name: string;
    isAllowEdit: boolean;
  };
  talkingPoints: string;
  token: string;
  version: ACVersions;
}

export interface Target {
  firstName: string;
  lastName: string;
  office: string;
  state: string;
  uuid: string;
}

export interface MatchedTarget extends Target {
  title: string;
  mappedMessage: Nullable<string>;
}

export interface SubjectMessagePair {
  isAllowPersonalMessage: boolean;
  body: Array<ContentBlockType>;
  subject: ContentBlockType;
  uuid: string;
}

export interface FormSubjectMessagePair extends SubjectMessagePair {
  personalMessage: string;
}

export type Targets = Array<Target>
export type MatchedTargets = Array<MatchedTarget>

export interface AutoCompleteAddress {
  streetLine: string;
  secondary: string;
  city: string;
  state: string;
  entries: number;
  zipcode: string;
}

export type AutoCompleteAddresses = Array<AutoCompleteAddress>

export type DataSuggestions = {
  suggestions: AutoCompleteAddresses;
}

type CustomFieldAnswers = {
  [key: string]: string;
}

export interface MessageForm {
  customFieldAnswers: CustomFieldAnswers;
  email: string;
  firstName: string;
  isOptInEmail: boolean;
  isOptInText: boolean;
  lastName: string;
  message: Array<string>;
  personalizedMessage: string;
  prefix: prefixTypes;
  phoneNumber: string;
  streetAddress: string & AutoCompleteAddress;
  subject: string;
  zipCode: string;
}

export type extendPrefixTypes = 'Ms' | 'Mr' | 'Mrs' | 'Dr' | 'Ms.' | 'Mr.' | 'Mrs.' | 'Dr.' | 'Miss' | '';

export interface ContactInfo {
  customFields: Array<CustomFieldValues>;
  email: string;
  firstName: string;
  isOptInEmail: boolean;
  isOptInText: boolean;
  lastName: string;
  point: {
    coordinates: [ number, number ];
    type: 'Point';
  };
  phoneNumber: string;
  prefix: extendPrefixTypes;
  streetAddress: string;
  streetAddress_2: string;
  zipCode: string;
  uuid?: string;
}

export interface ActionCenterPageProps {
  actionCenterDetails: ActionCenterDetails;
}

export interface TakeActionLandingPageProps extends ActionCenterPageProps {
  contactInfo: Nullable<ContactInfo>;
  nonGeocodedTargets: MatchedTargets;
}

export interface TakeActionWidgetProps extends ActionCenterPageProps {
  metaPixel?: string;
  nonGeocodedTargets: MatchedTargets;
}

export type prefixTypes = 'Ms.' | 'Mr.' | 'Mrs.' | 'Dr.' | 'Miss' | 'Mx.' | '';

interface ContactInfoForm {
  email: string;
  firstName: string;
  lastName: string;
  isOptInEmail: boolean;
  isOptInText: boolean;
  phoneNumber: number | string;
  prefix: prefixTypes;
  streetAddress: string;
  zipCode: string;
  uuid?: string;
}

export interface CustomFieldsForm {
  value?: string;
  values?: Array<string>;
  uuid: string;
}

export interface SignupFormPayload extends ContactInfoForm {
  customFields?: Array<CustomFieldsForm>;
  isOptInEmail: boolean;
  isOptInText: boolean;
  sessionId?: string;
}

export enum ActionCenterTypes {
  emailType = 'email',
  phoneType = 'phone',
}

export enum ActionResponseStatus {
  live = 'live',
  test = 'test',
  petition = 'petition',
}

export enum MessagePairTypes {
  messageMapping = 'mapping',
  messageRotation = 'rotation',
}

interface Meta {
  [key: string]: string;
}

interface SubjectMessagePayload {
  message?: string;
  uuid?: string;
  subject?: string;
  targets?: Array<string>;
}

export interface TakeActionFormPayload {
  action: ActionCenterTypes;
  contact: ContactInfoForm;
  sessionId?: string;
  customFields?: Array<CustomFieldsForm>;
  subject?: string;
  targets?: Array<string>;
  message?: string;
  messageId?: string;
  messages?: Array<SubjectMessagePayload>;
  meta?: Meta;
}

export interface SubmitResponse {
  message?: string;
  status?: string;
  targets: Targets;
}

export interface SubmitErrorResponse {
  contact?: {
    email?: Array<string>;
    first_name?: Array<string>;
    last_name?: Array<string>;
    phone_number?: Array<string>;
    prefix?: Array<string>;
    street_address?: Array<string>;
    zip_code?: Array<string>;
  };
  customFields?: Array<CustomFieldErrors>;
  subject?: Array<string>;
  message?: Array<string>;
  messages?: Array<MessagesErrorsFields>;
}

export interface ThankYouMessageProps {
  centered?: boolean;
}

export interface FormState {
  email: string;
  firstName: string;
  lastName: string;
  streetAddress: string;
  phoneNumber: string | number;
  prefix: prefixTypes;
  zipCode: string;
  customFields: Array<CustomFieldWithValue>;
  isOptInEmail: boolean;
  isOptInText: boolean;
}

export interface OfficialLookupFormState {
  streetAddress: string;
  zipCode: string;
}

export interface TakeActionFormState extends FormState {
  subjectMessagePairs: Array<FormSubjectMessagePair>;
}

export interface CustomFieldErrors {
  value: Array<string>;
}

export interface ErrorsFields {
  email?: Array<string>;
  firstName?: Array<string>;
  lastName?: Array<string>;
  phoneNumber?: Array<string>;
  prefix?: Array<string>;
  streetAddress?: Array<string>;
  zipCode?: Array<string>;
  customFields?: Array<CustomFieldErrors>;
  generalError?: string;
  submitError?: string;
}

export interface MessagesErrorsFields {
  message?: Array<string>;
  subject?: Array<string>;
}

export interface TakeActionErrorsFields extends ErrorsFields {
  messages?: Array<MessagesErrorsFields>;
  submitError?: string;
}

export type errorFieldsTypes = 'prefix' | 'firstName' | 'lastName' | 'zipCode' | 'email' | 'phoneNumber' | 'streetAddress';

export interface MatchTargetsPayload {
  streetAddress: string;
  zipCode: string;
}

// Used for managing which form fields are required
export interface RequiredFields {
  prefix?: boolean;
  firstName?: boolean;
  lastName?: boolean;
  streetAddress?: boolean;
  zipCode?: boolean;
  email?: boolean;
  phoneNumber?: boolean;
  custom?: boolean;
}

// Per-field help text revealed via each field's help toggle and
// announced to screen readers through aria-describedby. Supplied
// by the form container so the same fields component can show
// help in one context (e.g. V4 action centers) and not another.
// Custom fields are not listed here: their help text comes from
// the description authored when the field is created.
export interface FieldsHelpText {
  prefix?: string;
  firstName?: string;
  lastName?: string;
  streetAddress?: string;
  zipCode?: string;
  email?: string;
  phoneNumber?: string;
}
